import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { 
  Anchor, LayoutDashboard, Calendar, Users, DollarSign, RefreshCw, 
  Check, Clock, MapPin, Search, ChevronRight, Menu, X, LogOut,
  QrCode, CheckCircle2, Camera, XCircle, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { api, useAuth } from '../App';

const OperatorDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/operator', icon: LayoutDashboard, label: 'Overview', exact: true },
    { path: '/operator/bookings', icon: Calendar, label: 'Bookings' },
    { path: '/operator/checkin', icon: QrCode, label: 'Check-in' },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-border">
        <div className="p-6 border-b border-border">
          <Link to="/" className="flex items-center gap-2">
            <img src="/assets/logo.png" alt="The Village at Athol Island" className="h-10 w-auto" />
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(({ path, icon: Icon, label, exact }) => {
            const isActive = exact ? location.pathname === path : location.pathname.startsWith(path);
            return (
              <Link
                key={path}
                to={path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-primary/10 text-primary font-medium' 
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                <Icon className="w-5 h-5" />
                {label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <span className="text-primary font-bold">{user?.name?.[0] || 'O'}</span>
            </div>
            <div>
              <p className="font-medium text-sm">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
          </div>
          <Link to="/change-password" className="block">
            <Button variant="ghost" className="w-full justify-start text-muted-foreground">
              <Lock className="w-4 h-4 mr-2" />
              Change Password
            </Button>
          </Link>
          <Button variant="ghost" className="w-full justify-start text-muted-foreground" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-border z-50">
        <div className="flex items-center justify-between p-4">
          <button onClick={() => setSidebarOpen(true)} className="p-2">
            <Menu className="w-6 h-6" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <img src="/assets/logo.png" alt="The Village at Athol Island" className="h-8 w-auto" />
          </Link>
          <div className="w-10" />
        </div>
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <span className="font-serif font-bold">Menu</span>
              <button onClick={() => setSidebarOpen(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="p-4 space-y-1">
              {navItems.map(({ path, icon: Icon, label, exact }) => {
                const isActive = exact ? location.pathname === path : location.pathname.startsWith(path);
                return (
                  <Link
                    key={path}
                    to={path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive 
                        ? 'bg-primary/10 text-primary font-medium' 
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {label}
                  </Link>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:pt-0 pt-16">
        <Routes>
          <Route index element={<DashboardOverview />} />
          <Route path="bookings" element={<BookingsTab />} />
          <Route path="checkin" element={<CheckinTab />} />
        </Routes>
      </main>
    </div>
  );
};

// Dashboard Overview
const DashboardOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const response = await api.get('/dashboard/operator');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleConfirm = async (bookingId) => {
    try {
      await api.put(`/bookings/${bookingId}/status?status=confirmed`);
      toast.success('Booking confirmed');
      fetchData();
    } catch (error) {
      toast.error('Failed to confirm booking');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl lg:text-3xl font-bold" data-testid="operator-dashboard-title">Operator Dashboard</h1>
          <p className="text-muted-foreground">Welcome back, {data?.stats?.name || 'The Village at Athol Island'}</p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
        <KPICard 
          icon={Anchor} 
          label="Active Tour" 
          value={data?.stats?.active_tours || 0}
          color="teal"
        />
        <KPICard 
          icon={Clock} 
          label="Pending Confirmation" 
          value={data?.stats?.pending_confirmation || 0}
          color="yellow"
        />
        <KPICard 
          icon={CheckCircle2} 
          label="Confirmed Bookings" 
          value={data?.stats?.confirmed_bookings || 0}
          color="green"
        />
        <KPICard 
          icon={DollarSign} 
          label="Net Earnings" 
          value={`$${(data?.stats?.net_earnings || 0).toFixed(2)}`}
          color="coral"
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="bookings" className="relative">
            Bookings
            {data?.stats?.pending_confirmation > 0 && (
              <span className="ml-2 w-5 h-5 bg-yellow-400 text-yellow-900 rounded-full text-xs flex items-center justify-center">
                {data.stats.pending_confirmation}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="tours">My Tour</TabsTrigger>
          <TabsTrigger value="earnings">Earnings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pending Confirmations */}
            <div className="bg-white rounded-xl border border-border p-6">
              <h3 className="font-serif font-bold text-lg mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-500" />
                Pending Confirmation
              </h3>
              {data?.pending_confirmations?.length > 0 ? (
                <div className="space-y-3">
                  {data.pending_confirmations.slice(0, 5).map((booking) => (
                    <div key={booking.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div>
                        <p className="font-medium">{booking.customer_name}</p>
                        <p className="text-sm text-muted-foreground">{booking.tour_title}</p>
                        <p className="text-xs text-muted-foreground">{booking.date} at {booking.time_slot}</p>
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-green-500 hover:bg-green-600"
                        onClick={() => handleConfirm(booking.id)}
                        data-testid={`confirm-${booking.id}`}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No pending confirmations</p>
              )}
            </div>

            {/* Recent Bookings */}
            <div className="bg-white rounded-xl border border-border p-6">
              <h3 className="font-serif font-bold text-lg mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Recent Bookings
              </h3>
              {data?.recent_bookings?.length > 0 ? (
                <div className="space-y-3">
                  {data.recent_bookings.slice(0, 5).map((booking) => (
                    <div key={booking.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div>
                        <p className="font-medium">{booking.customer_name}</p>
                        <p className="text-sm text-muted-foreground">{booking.tour_title}</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          booking.status === 'confirmed' ? 'badge-confirmed' : 'badge-pending'
                        }`}>
                          {booking.status}
                        </span>
                        <p className="text-sm font-semibold text-primary mt-1">${booking.total_amount}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No recent bookings</p>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="bookings">
          <BookingsTab />
        </TabsContent>

        <TabsContent value="tours">
          <div className="text-center py-8 text-muted-foreground">
            Tour management coming soon
          </div>
        </TabsContent>

        <TabsContent value="earnings">
          <div className="text-center py-8 text-muted-foreground">
            Earnings report coming soon
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// KPI Card Component
const KPICard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    teal: 'bg-teal-light text-primary',
    yellow: 'bg-yellow-100 text-yellow-600',
    green: 'bg-green-100 text-green-600',
    coral: 'bg-coral-light text-coral-dark'
  };

  return (
    <div className="bg-white rounded-xl border border-border p-5">
      <div className={`w-12 h-12 rounded-full ${colorClasses[color]} flex items-center justify-center mb-3`}>
        <Icon className="w-6 h-6" />
      </div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
};

// Bookings Tab
const BookingsTab = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await api.get('/bookings');
        setBookings(response.data);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, []);

  const filteredBookings = bookings.filter(b =>
    b.customer_name.toLowerCase().includes(search.toLowerCase()) ||
    b.tour_title.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-0">
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search bookings..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left p-4 font-semibold text-sm">Customer</th>
                <th className="text-left p-4 font-semibold text-sm">Tour</th>
                <th className="text-left p-4 font-semibold text-sm">Date</th>
                <th className="text-left p-4 font-semibold text-sm">Status</th>
                <th className="text-left p-4 font-semibold text-sm">Amount</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings.map((booking) => (
                <tr key={booking.id} className="border-t border-border hover:bg-muted/50">
                  <td className="p-4">
                    <p className="font-medium">{booking.customer_name}</p>
                    <p className="text-sm text-muted-foreground">{booking.customer_email}</p>
                  </td>
                  <td className="p-4">{booking.tour_title}</td>
                  <td className="p-4">
                    <p>{booking.date}</p>
                    <p className="text-sm text-muted-foreground">{booking.time_slot}</p>
                  </td>
                  <td className="p-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      booking.status === 'confirmed' ? 'badge-confirmed' :
                      booking.status === 'completed' ? 'badge-completed' :
                      booking.status === 'cancelled' ? 'badge-cancelled' : 'badge-pending'
                    }`}>
                      {booking.status}
                    </span>
                  </td>
                  <td className="p-4 font-semibold">${booking.total_amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Check-in Tab
const CheckinTab = () => {
  const [bookingId, setBookingId] = useState('');
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  const handleSearch = useCallback(async (id) => {
    const searchId = id || bookingId;
    if (!searchId) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/bookings/${searchId}`);
      setBooking(response.data);
    } catch (error) {
      toast.error('Booking not found');
      setBooking(null);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  const stopScanner = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (e) {
        // Scanner may already be stopped
      }
      html5QrCodeRef.current = null;
    }
    setScannerActive(false);
  }, []);

  const startScanner = useCallback(async () => {
    setScannerActive(true);
    setBooking(null);

    // Dynamic import to avoid SSR issues
    const { Html5Qrcode } = await import('html5-qrcode');
    
    // Small delay to let the DOM render the scanner container
    await new Promise(r => setTimeout(r, 300));
    
    if (!scannerRef.current) return;
    
    const scanner = new Html5Qrcode("qr-reader");
    html5QrCodeRef.current = scanner;

    try {
      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        async (decodedText) => {
          // Extract booking ID from QR content
          let extractedId = decodedText;
          if (decodedText.startsWith('BOOKING:')) {
            extractedId = decodedText.replace('BOOKING:', '');
          }
          
          setBookingId(extractedId);
          await stopScanner();
          toast.success('QR Code scanned successfully!');
          
          // Auto-search the scanned booking
          setLoading(true);
          try {
            const response = await api.get(`/bookings/${extractedId}`);
            setBooking(response.data);
          } catch (error) {
            toast.error('Booking not found for scanned QR code');
            setBooking(null);
          } finally {
            setLoading(false);
          }
        },
        () => {
          // QR code scan error (no code found in frame) - ignore silently
        }
      );
    } catch (err) {
      toast.error('Unable to access camera. Please check permissions or enter the booking ID manually.');
      setScannerActive(false);
    }
  }, [stopScanner]);

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        try {
          html5QrCodeRef.current.stop();
        } catch (e) {}
      }
    };
  }, []);

  const handleCheckin = async () => {
    try {
      await api.post(`/bookings/${booking.id}/checkin`);
      toast.success('Check-in successful!');
      setBooking(prev => ({ ...prev, status: 'completed' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    }
  };

  return (
    <div className="p-6 lg:p-8">
      <h2 className="font-serif text-2xl font-bold mb-6">Guest Check-in</h2>

      <div className="max-w-lg mx-auto">
        {/* Search Bar + Scan Button */}
        <div className="bg-white rounded-xl border border-border p-6 mb-6">
          <div className="flex gap-3 mb-4">
            <Input
              placeholder="Enter booking ID..."
              value={bookingId}
              onChange={(e) => setBookingId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1"
              data-testid="checkin-booking-id-input"
            />
            <Button onClick={() => handleSearch()} disabled={loading} data-testid="checkin-search-btn">
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </div>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-3 text-muted-foreground">or</span>
            </div>
          </div>

          <div className="mt-4">
            {!scannerActive ? (
              <Button
                onClick={startScanner}
                variant="outline"
                className="w-full py-5 border-dashed border-2 hover:border-primary hover:bg-primary/5 transition-all"
                data-testid="scan-qr-btn"
              >
                <Camera className="w-5 h-5 mr-2 text-primary" />
                <span className="text-primary font-medium">Scan QR Code</span>
              </Button>
            ) : (
              <div className="space-y-3">
                <div className="relative rounded-xl overflow-hidden border-2 border-primary bg-black">
                  <div id="qr-reader" ref={scannerRef} className="w-full" data-testid="qr-scanner" />
                </div>
                <Button
                  onClick={stopScanner}
                  variant="outline"
                  className="w-full"
                  data-testid="stop-scanner-btn"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancel Scan
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Booking Result */}
        {booking && (
          <div className="bg-white rounded-xl border border-border p-6" data-testid="checkin-booking-result">
            <div className="text-center mb-6">
              {booking.status === 'completed' ? (
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
              ) : (
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <QrCode className="w-8 h-8 text-primary" />
                </div>
              )}
              <h3 className="font-serif font-bold text-xl">{booking.customer_name}</h3>
              <p className="text-muted-foreground">{booking.tour_title}</p>
              <p className="text-xs text-muted-foreground mt-1 font-mono">REF: {booking.id.slice(0, 8).toUpperCase()}</p>
            </div>

            <div className="space-y-3 text-sm mb-6">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Date</span>
                <span className="font-medium">{booking.date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Time</span>
                <span className="font-medium">{booking.time_slot}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Guests</span>
                <span className="font-medium">
                  {booking.adults} Adult{booking.adults !== 1 ? 's' : ''}
                  {booking.children > 0 && `, ${booking.children} Child${booking.children !== 1 ? 'ren' : ''}`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-medium">BSD ${booking.total_amount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className={`font-medium px-2 py-0.5 rounded-full text-xs ${
                  booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                  booking.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                  booking.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Payment</span>
                <span className={`font-medium ${
                  booking.payment_status === 'fully_paid' ? 'text-green-600' : 
                  booking.payment_status === 'pay_on_arrival' ? 'text-amber-600' : 'text-yellow-600'
                }`}>
                  {booking.payment_status === 'fully_paid' ? 'Fully Paid' : 
                   booking.payment_status === 'pay_on_arrival' ? 'Pay on Arrival' : 'Pending'}
                </span>
              </div>
            </div>

            {booking.status !== 'completed' && (booking.payment_status === 'fully_paid' || booking.payment_status === 'pay_on_arrival') && (
              <Button className="w-full btn-primary" onClick={handleCheckin} data-testid="complete-checkin-btn">
                <Check className="w-4 h-4 mr-2" />
                Complete Check-in
              </Button>
            )}

            {booking.status === 'completed' && (
              <div className="text-center text-green-600 font-medium flex items-center justify-center gap-2" data-testid="already-checked-in">
                <CheckCircle2 className="w-5 h-5" />
                Already checked in
              </div>
            )}

            {booking.payment_status !== 'fully_paid' && booking.payment_status !== 'pay_on_arrival' && (
              <div className="text-center text-yellow-600 text-sm">
                Balance payment required before check-in
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OperatorDashboard;
